import type { DefaultContext } from "@apollo/client";
import { useApolloClient, useLazyQuery } from "@apollo/client/react";
import { useQueryClient } from "@tanstack/react-query";
import { use, useCallback, useMemo } from "react";

import {
  getFeedPostIdList,
  type GetFeedPostIDListResponse,
} from "@/backend/focus";
import type { PostType } from "@/contexts/feed-context";
import { useFeedContext } from "@/contexts/feed-context";
import { UserContext } from "@/contexts/user-context";
import type {
  Post,
  PostCondition,
  PostFilter,
  PostsQuery,
  PostStatCondition,
  PostStatFilter,
} from "@/graphql/codegen/graphql";
import {
  AccountByPublicKeyCachedDocument,
  FollowFeedsHashesDocument,
  FollowFeedsOrderBy,
  PostByPostHashCachedDocument,
  PostsHashesDocument,
  PostsOrderBy,
  PostStatsHashesDocument,
  PostStatsOrderBy,
} from "@/graphql/codegen/graphql";
import { useFocusPreferences } from "@/hooks/use-focus-preferences";
import { useImageMeta } from "@/hooks/use-image-meta";
import { useGetClientCookiesValues } from "@/utils/client-utils";
import { AssociationType, POST_LIMIT } from "@/utils/constants";
import {
  feedFreezeDebugLog,
  getFeedFreezeDebugDuration,
} from "@/utils/feed-freeze-debug";
import {
  buildCustomFeedCondition,
  buildCustomFeedFilter,
  buildCustomFeedPostStatsCondition,
  buildCustomFeedPostStatsFilter,
  type FeedQueryPresetId,
  getCustomFeedPostsOrderBy,
  getCustomFeedPostStatsOrderBy,
  getCustomFeedQuerySource,
  getFeedQueryPreset,
} from "@/utils/feed-query-presets";
import {
  type FocusCustomFeed,
  type FocusCustomFeedPreferenceOverrides,
  mergeFeedPreferencesWithOverrides,
} from "@/utils/focus-preferences";
import { parseError } from "@/utils/helpers";
import {
  getMyFeedPostIdsQueryKey,
  MY_FEED_POST_IDS_CACHE_MAX_AGE_MS,
  type MyFeedPostIdsQueryParams,
} from "@/utils/query-persistence";

const MAIN_FEED_BASE_FILTERS = {
  parentPostHash: {
    isNull: true,
  },
  isHidden: {
    equalTo: false,
  },
};

const hasNonEmptyFilterValue = (value: unknown): boolean => {
  if (Array.isArray(value)) {
    return value.length > 0;
  }

  if (value && typeof value === "object") {
    return Object.values(value).some((entry) => hasNonEmptyFilterValue(entry));
  }

  return value !== undefined;
};

const isNonEmptyPostFilter = (
  filter: PostFilter | undefined,
): filter is PostFilter => {
  return hasNonEmptyFilterValue(filter);
};

const isNonEmptyPostStatFilter = (
  filter: PostStatFilter | undefined,
): filter is PostStatFilter => {
  return hasNonEmptyFilterValue(filter);
};

const combinePostFilters = (
  ...filters: Array<PostFilter | undefined>
): PostFilter | undefined => {
  const validFilters = filters.filter(isNonEmptyPostFilter);

  if (validFilters.length === 0) {
    return undefined;
  }

  if (validFilters.length === 1) {
    return validFilters[0];
  }

  return {
    and: validFilters,
  };
};

const combinePostConditions = (
  ...conditions: Array<PostCondition | undefined>
): PostCondition | undefined => {
  const validConditions = conditions.filter((condition) => !!condition);

  if (validConditions.length === 0) {
    return undefined;
  }

  return Object.assign({}, ...validConditions);
};

const combinePostStatFilters = (
  ...filters: Array<PostStatFilter | undefined>
): PostStatFilter | undefined => {
  const validFilters = filters.filter(isNonEmptyPostStatFilter);

  if (validFilters.length === 0) {
    return undefined;
  }

  if (validFilters.length === 1) {
    return validFilters[0];
  }

  return {
    and: validFilters,
  };
};

const wrapPostFilterInPostStatsFilter = (
  filter?: PostFilter,
): PostStatFilter | undefined => {
  return hasNonEmptyFilterValue(filter)
    ? ({
        post: filter,
      } as PostStatFilter)
    : undefined;
};

const postConditionToFilter = (
  condition?: PostCondition,
): PostFilter | undefined => {
  if (!condition) {
    return undefined;
  }

  const entries = Object.entries(condition).filter(([, value]) => {
    return value !== undefined;
  });

  if (entries.length === 0) {
    return undefined;
  }

  return Object.fromEntries(
    entries.map(([field, value]) => [
      field,
      {
        equalTo: value,
      },
    ]),
  ) as PostFilter;
};

type QueryExecutionResult<TData extends Record<string, unknown>> = {
  data?: TData | null;
  error?: Error | null;
  errors?: ReadonlyArray<{ message?: string | null }> | null;
};

const getQueryExecutionError = <TData extends Record<string, unknown>>(
  result: QueryExecutionResult<TData>,
  rootField: keyof TData & string,
  queryLabel: string,
): Error | undefined => {
  if (result.error) {
    return result.error;
  }

  const graphQLErrors = result.errors?.filter(Boolean) ?? [];
  if (graphQLErrors.length > 0) {
    const message = graphQLErrors
      .map((error) => error.message)
      .filter(Boolean)
      .join("\n");

    return new Error(message || `${queryLabel} failed.`);
  }

  if (result.data && result.data[rootField] === null) {
    return new Error(`${queryLabel} returned no data.`);
  }

  return undefined;
};

const assertQueryExecutionSucceeded = <TData extends Record<string, unknown>>(
  result: QueryExecutionResult<TData>,
  rootField: keyof TData & string,
  queryLabel: string,
) => {
  const error = getQueryExecutionError(result, rootField, queryLabel);

  if (error) {
    throw error;
  }
};

export type GetPostsDTO = {
  condition?: PostCondition;
  filters?: PostFilter;
  offset?: number;
  orderBy?: PostsOrderBy | PostsOrderBy[];
  context?: DefaultContext;
  feedPreferencesOverrides?: FocusCustomFeedPreferenceOverrides;
};

export type GetPostsFromListDTO = GetPostsDTO & {
  listId?: string;
  listName?: string | null;
};

type GetPostStatsDTO = {
  condition?: PostStatCondition;
  filters?: PostStatFilter;
  offset?: number;
  orderBy?: PostStatsOrderBy | PostStatsOrderBy[];
  context?: DefaultContext;
};

const MAX_REPOST_DEPTH = 2;

export const getNonBlockFilter = (publicKey: string) => {
  return {
    and: [
      {
        userAssociationsAsTarget: {
          none: {
            associationType: {
              equalTo: AssociationType.block,
            },
            transactor: {
              publicKey: {
                equalTo: publicKey,
              },
            },
          },
        },
      },
      {
        userAssociationsAsTransactor: {
          none: {
            associationType: {
              equalTo: AssociationType.block,
            },
            target: {
              publicKey: {
                equalTo: publicKey,
              },
            },
          },
        },
      },
    ],
  };
};

export const useGetPosts = () => {
  // temp
  const feedContext = useFeedContext();
  const { preferences } = useFocusPreferences();
  const tempFilters = feedContext?.tempFilters ?? preferences.feedPreferences;
  const apolloClient = useApolloClient();
  const queryClient = useQueryClient();

  const [_getPostsHashes] = useLazyQuery(PostsHashesDocument, {
    // These helpers are used as imperative awaited fetches for first-paint feed
    // state, so they must resolve with the fresh network payload instead of a
    // stale cached snapshot that updates later.
    fetchPolicy: "network-only",
  });
  const [_getFollowFeedHashes] = useLazyQuery(FollowFeedsHashesDocument, {
    fetchPolicy: "network-only",
  });
  const [_getPostStatsHashes] = useLazyQuery(PostStatsHashesDocument, {
    fetchPolicy: "network-only",
  });

  const { getImageMeta } = useImageMeta();
  const { currentUserPublicKey, loggedIn } = use(UserContext);
  const { publicKey: cookiePublicKey } = useGetClientCookiesValues();
  const effectivePublicKey =
    currentUserPublicKey || (loggedIn ? cookiePublicKey : "");
  const [getAccountByPublicKey] = useLazyQuery(
    AccountByPublicKeyCachedDocument,
  );

  const NON_BLOCKED_FILTER = useMemo(
    () => getNonBlockFilter(effectivePublicKey),
    [effectivePublicKey],
  );

  const getImageMetaProcessing = useCallback(
    (posts: Post[]) => {
      const postsWithImages = posts
        .filter((post) => post?.imageUrls || post?.repostedPost?.imageUrls)
        .filter(Boolean);

      for (const post of postsWithImages) {
        if (post.imageUrls) {
          const imageUrls = post.imageUrls.filter(Boolean);
          getImageMeta(imageUrls);
        }

        if (post.repostedPost?.imageUrls) {
          const repostedPostImageUrls =
            post.repostedPost.imageUrls.filter(Boolean);
          getImageMeta(repostedPostImageUrls);
        }
      }
    },
    [getImageMeta],
  );

  const fetchPostWithReposts = useCallback(
    async function fetchPostRecursive(
      postHash: string,
      level = 0,
    ): Promise<Post | null> {
      if (level > MAX_REPOST_DEPTH) return null;

      const response = await apolloClient.query({
        query: PostByPostHashCachedDocument,
        variables: { postHash },
      });

      let post = response?.data?.postByPostHash as Post;
      if (!post) return null;

      if (post.repostedPostHash) {
        const repostedPost = await fetchPostRecursive(
          post.repostedPostHash,
          level + 1,
        );
        if (repostedPost) {
          post = { ...post, repostedPost: repostedPost };
        }
      }

      return post;
    },
    [apolloClient],
  );

  const getPostFromPostHashes = useCallback(
    async (postHashes: Array<string | null | undefined>) => {
      const requestedHashes = postHashes.filter(Boolean);
      const startedAt = performance.now();
      const postDetailsPromises = postHashes.map((postHash) => {
        if (!postHash) return Promise.resolve(null);
        return fetchPostWithReposts(postHash);
      });

      const detailedPosts = await Promise.all(postDetailsPromises);
      const hydratedPosts = detailedPosts.filter(Boolean);

      if (hydratedPosts.length !== requestedHashes.length) {
        feedFreezeDebugLog(
          "feed-bootstrap",
          "hydrate:mismatch",
          {
            durationMs: getFeedFreezeDebugDuration(startedAt),
            hydratedCount: hydratedPosts.length,
            requestedCount: requestedHashes.length,
            requestedPostHashes: requestedHashes,
          },
          {
            level: "warn",
          },
        );
      } else {
        feedFreezeDebugLog("feed-bootstrap", "hydrate:complete", {
          durationMs: getFeedFreezeDebugDuration(startedAt),
          hydratedCount: hydratedPosts.length,
          requestedCount: requestedHashes.length,
        });
      }

      return hydratedPosts;
    },
    [fetchPostWithReposts],
  );

  const getPosts = useCallback(
    async ({
      condition,
      filters,
      orderBy = PostsOrderBy.TimestampDesc,
      offset = 0,
      context,
    }: GetPostsDTO): Promise<PostsQuery | undefined> => {
      const startedAt = performance.now();
      const normalizedOrderBy = [
        ...(Array.isArray(orderBy) ? orderBy : [orderBy]),
        PostsOrderBy.PostHashDesc,
      ].filter((value, index, values) => values.indexOf(value) === index);

      const postsHashesRequest = _getPostsHashes({
        variables: {
          first: POST_LIMIT,
          orderBy: normalizedOrderBy,
          postCondition: condition,
          postFilter: filters,
          offset,
        },
        context,
      });
      postsHashesRequest.retain?.();
      const postsHashesResponse = await postsHashesRequest;
      assertQueryExecutionSucceeded(
        postsHashesResponse,
        "posts",
        "Posts hash query",
      );

      const { posts } = postsHashesResponse?.data || {};
      if (posts) {
        const { nodes } = posts;

        if (!nodes) return;

        feedFreezeDebugLog("feed-bootstrap", "hashes:complete", {
          durationMs: getFeedFreezeDebugDuration(startedAt),
          hashCount: nodes.length,
          offset,
          orderBy: normalizedOrderBy,
        });

        const detailedPosts = await getPostFromPostHashes(
          nodes.map((x) => x?.postHash),
        );

        getImageMetaProcessing(detailedPosts as Post[]);
        return {
          ...postsHashesResponse.data,
          posts: {
            ...posts,
            nodes: detailedPosts,
          },
        } as PostsQuery;
      }
    },
    // note - do not change this dependency array
    [_getPostsHashes, getPostFromPostHashes, getImageMetaProcessing],
  );

  const getPostsFromPostStats = useCallback(
    async ({
      condition,
      filters,
      orderBy = PostStatsOrderBy.TotalReactionCountDesc,
      offset = 0,
      context,
    }: GetPostStatsDTO) => {
      const normalizedOrderBy = [
        ...(Array.isArray(orderBy) ? orderBy : [orderBy]),
        PostStatsOrderBy.PostHashDesc,
      ].filter((value, index, values) => values.indexOf(value) === index);

      const postStatsHashesRequest = _getPostStatsHashes({
        variables: {
          first: POST_LIMIT,
          orderBy: normalizedOrderBy,
          postStatCondition: condition,
          postStatFilter: filters,
          offset,
        },
        context,
      });
      postStatsHashesRequest.retain?.();
      const postStatsHashesResponse = await postStatsHashesRequest;
      assertQueryExecutionSucceeded(
        postStatsHashesResponse,
        "postStats",
        "Post stats hash query",
      );

      const postHashes = postStatsHashesResponse?.data?.postStats?.nodes
        .map((entry) => entry?.postHash)
        .filter(Boolean);

      if (!postHashes?.length) {
        return [];
      }

      const detailedPosts = await getPostFromPostHashes(postHashes);
      getImageMetaProcessing(detailedPosts as Post[]);

      return detailedPosts as Post[];
    },
    [_getPostStatsHashes, getImageMetaProcessing, getPostFromPostHashes],
  );

  const getMyFeedPostIdData = useCallback(
    async (params: MyFeedPostIdsQueryParams) => {
      const queryKey = getMyFeedPostIdsQueryKey(params);
      const queryState =
        queryClient.getQueryState<GetFeedPostIDListResponse>(queryKey);
      const cachedData = queryState?.data;
      const cacheAge = Date.now() - (queryState?.dataUpdatedAt ?? 0);

      const fetchLatest = () =>
        queryClient.fetchQuery({
          queryKey,
          queryFn: () => getFeedPostIdList(params),
          gcTime: MY_FEED_POST_IDS_CACHE_MAX_AGE_MS,
          staleTime: 0,
        });

      if (cachedData && cacheAge <= MY_FEED_POST_IDS_CACHE_MAX_AGE_MS) {
        void fetchLatest().catch((error) => {
          feedFreezeDebugLog(
            "feed-bootstrap",
            "for-you:hash-cache-refresh-error",
            {
              error: parseError(error),
              limit: params.limit,
              offset: params.offset,
              publicKey: params.publicKey,
            },
            { level: "warn" },
          );
        });

        return cachedData;
      }

      return fetchLatest();
    },
    [queryClient],
  );

  const getPostsForYouFeed = useCallback(
    async ({ limit, page }: { limit: number; page: number }) => {
      const startedAt = performance.now();

      feedFreezeDebugLog("feed-bootstrap", "for-you:start", {
        cookiePublicKey: cookiePublicKey || null,
        currentUserPublicKey: currentUserPublicKey || null,
        effectivePublicKey: effectivePublicKey || null,
        limit,
        page,
        tempFilters,
      });

      try {
        const postIdData = await getMyFeedPostIdData({
          limit,
          offset: page * limit,
          publicKey: effectivePublicKey || "default",
          hideViewed: tempFilters.hideViewed,
          hideNSFW: tempFilters.hideNSFW === "hide",
          useModeration: tempFilters.useModeration,
        });

        feedFreezeDebugLog("feed-bootstrap", "for-you:hashes", {
          durationMs: getFeedFreezeDebugDuration(startedAt),
          effectivePublicKey: effectivePublicKey || "default",
          feedName: postIdData.feedName,
          hashCount: postIdData.postHashes.length,
          page,
        });

        const feedProviderRequest = getAccountByPublicKey({
          variables: {
            publicKey: postIdData.feedProviderPublicKey,
          },
        });
        feedProviderRequest.retain?.();
        const { data: feedProvider } = await feedProviderRequest;

        const data = await getPosts({
          filters: {
            postHash: {
              in: postIdData?.postHashes || [],
            },
          },
          orderBy: PostsOrderBy.TimestampDesc,
        });

        // Make sure posts are ordered in the same order as the original feed.
        let posts = (data?.posts?.nodes.filter(Boolean) || []).sort(
          (a, b) =>
            postIdData.postHashes.indexOf(a.postHash) -
            postIdData.postHashes.indexOf(b.postHash),
        );

        if (tempFilters.hideNSFW === "hide") {
          posts = posts.filter((post) => {
            return (
              post.nsfwCount?.nodes.length === 0 &&
              post.poster?.isNSFW.totalCount === 0
            );
          });
        }

        feedFreezeDebugLog("feed-bootstrap", "for-you:complete", {
          durationMs: getFeedFreezeDebugDuration(startedAt),
          effectivePublicKey: effectivePublicKey || "default",
          feedName: postIdData.feedName,
          filteredCount: posts.length,
          hydratedCount: data?.posts?.nodes.filter(Boolean).length ?? 0,
          page,
        });

        return {
          posts,
          feedName: postIdData?.feedName,
          feedProvider,
        };
      } catch (error) {
        feedFreezeDebugLog(
          "feed-bootstrap",
          "for-you:error",
          {
            durationMs: getFeedFreezeDebugDuration(startedAt),
            effectivePublicKey: effectivePublicKey || "default",
            error:
              error instanceof Error ? error.message : JSON.stringify(error),
            page,
          },
          { level: "error" },
        );
        throw error instanceof Error ? error : new Error(parseError(error));
      }
    },
    [
      effectivePublicKey,
      getAccountByPublicKey,
      getMyFeedPostIdData,
      getPosts,
      tempFilters,
    ],
  );

  const getPostsFromFollowingPeople = useCallback(
    async ({ offset = 0 }: GetPostsDTO = {}) => {
      const postsHashesRequest = _getFollowFeedHashes({
        variables: {
          first: POST_LIMIT,
          orderBy: [FollowFeedsOrderBy.TimestampDesc],
          filter: {
            followerPublicKey: {
              equalTo: effectivePublicKey,
            },
          },
          offset,
        },
        context: {
          bypassBatch: true, // Bypass the batch request middleware
        },
      });
      postsHashesRequest.retain?.();
      const postsHashesResponse = await postsHashesRequest;
      assertQueryExecutionSucceeded(
        postsHashesResponse,
        "followFeeds",
        "Following feed hash query",
      );

      const postsHashes = postsHashesResponse?.data?.followFeeds?.nodes
        .map((x) => x?.postHash)
        .filter(Boolean);

      let posts: Awaited<ReturnType<typeof getPostFromPostHashes>> = [];

      if (postsHashes?.length) {
        posts = await getPostFromPostHashes(postsHashes);

        getImageMetaProcessing(posts as Post[]);
      }

      if (tempFilters.hideNSFW === "hide") {
        posts = posts.filter((post) => {
          return (
            post.nsfwCount?.nodes?.length === 0 &&
            post.poster?.isNSFW?.totalCount === 0
          );
        });
      }

      if (tempFilters.useModeration) {
        posts = posts.filter((post) => {
          return !post.poster?.isBlacklisted;
        });
      }

      return {
        posts,
      };
    },
    [
      _getFollowFeedHashes,
      effectivePublicKey,
      getPostFromPostHashes,
      getImageMetaProcessing,
      tempFilters,
    ],
  );

  const getPostsFromPreset = useCallback(
    async (
      presetId: FeedQueryPresetId,
      {
        condition,
        filters,
        orderBy,
        offset = 0,
        context,
        feedPreferencesOverrides,
      }: GetPostsDTO = {},
    ) => {
      const preset = getFeedQueryPreset(presetId);
      const effectiveFeedPreferences = mergeFeedPreferencesWithOverrides(
        tempFilters,
        feedPreferencesOverrides,
      );
      const data = await getPosts({
        condition,
        filters: combinePostFilters(
          {
            poster: {
              ...NON_BLOCKED_FILTER,
            },
            ...MAIN_FEED_BASE_FILTERS,
          },
          preset.buildFilter(),
          filters,
        ),
        orderBy:
          orderBy && preset.allowCustomOrderBy !== false
            ? orderBy
            : preset.orderBy,
        offset,
        context,
      });

      let posts = data?.posts?.nodes.filter(Boolean) || [];

      if (effectiveFeedPreferences.hideNSFW === "hide") {
        posts = posts.filter((post) => {
          return (
            post.nsfwCount?.nodes?.length === 0 &&
            post.poster?.isNSFW.totalCount === 0
          );
        });
      }

      if (effectiveFeedPreferences.useModeration) {
        posts = posts.filter((post) => {
          return !post.poster?.isBlacklisted;
        });
      }

      return {
        posts,
      };
    },
    [NON_BLOCKED_FILTER, getPosts, tempFilters],
  );

  const getPostsFromCustomFeed = useCallback(
    async (
      customFeed: FocusCustomFeed,
      {
        condition,
        filters,
        orderBy,
        offset = 0,
        context,
        feedPreferencesOverrides,
      }: GetPostsDTO = {},
    ) => {
      const effectiveFeedPreferences = mergeFeedPreferencesWithOverrides(
        mergeFeedPreferencesWithOverrides(
          tempFilters,
          customFeed.feedPreferencesOverrides,
        ),
        feedPreferencesOverrides,
      );
      const customFeedSource = getCustomFeedQuerySource(customFeed.query);
      let posts: PostType[] = [];

      if (customFeedSource === "post-stats") {
        posts = await getPostsFromPostStats({
          condition: buildCustomFeedPostStatsCondition(customFeed.query),
          filters: combinePostStatFilters(
            {
              post: {
                poster: {
                  ...NON_BLOCKED_FILTER,
                },
                ...MAIN_FEED_BASE_FILTERS,
              },
            },
            buildCustomFeedPostStatsFilter(customFeed.query),
            wrapPostFilterInPostStatsFilter(postConditionToFilter(condition)),
            wrapPostFilterInPostStatsFilter(filters),
          ),
          orderBy: getCustomFeedPostStatsOrderBy(customFeed.query),
          offset,
          context,
        });
      } else {
        const data = await getPosts({
          condition: combinePostConditions(
            buildCustomFeedCondition(customFeed.query),
            condition,
          ),
          filters: combinePostFilters(
            {
              poster: {
                ...NON_BLOCKED_FILTER,
              },
              ...MAIN_FEED_BASE_FILTERS,
            },
            buildCustomFeedFilter(customFeed.query),
            filters,
          ),
          orderBy: orderBy ?? getCustomFeedPostsOrderBy(customFeed.query),
          offset,
          context,
        });

        posts = data?.posts?.nodes.filter(Boolean) || [];
      }

      if (effectiveFeedPreferences.hideNSFW === "hide") {
        posts = posts.filter((post) => {
          return (
            post.nsfwCount?.nodes?.length === 0 &&
            post.poster?.isNSFW?.totalCount === 0
          );
        });
      }

      if (effectiveFeedPreferences.useModeration) {
        posts = posts.filter((post) => {
          return !post.poster?.isBlacklisted;
        });
      }

      return {
        posts,
      };
    },
    [NON_BLOCKED_FILTER, getPosts, getPostsFromPostStats, tempFilters],
  );

  const getPostsNew = useCallback(
    async (params: GetPostsDTO = {}) => getPostsFromPreset("new", params),
    [getPostsFromPreset],
  );

  const getPostsFromSubscribers = useCallback(
    async ({
      condition,
      filters,
      orderBy = PostsOrderBy.TimestampDesc,
      offset = 0,
    }: GetPostsDTO = {}) => {
      const data = await getPosts({
        condition,
        filters: {
          subscriptionAccessGroup: {
            and: [
              {
                accessGroupOwnerPublicKey: {
                  notEqualTo: effectivePublicKey,
                },
              },
              {
                accessGroupMembers: {
                  some: {
                    accessGroupMemberPublicKey: {
                      equalTo: effectivePublicKey,
                    },
                  },
                },
              },
            ],
          },
          poster: {
            ...NON_BLOCKED_FILTER,
          },
          ...MAIN_FEED_BASE_FILTERS,
          ...filters,
        },
        orderBy,
        offset,
      });

      let posts = data?.posts?.nodes.filter(Boolean) || [];

      if (tempFilters.hideNSFW === "hide") {
        posts = posts.filter((post) => {
          return (
            post.nsfwCount?.nodes.length === 0 &&
            post.poster?.isNSFW.totalCount === 0
          );
        });
      }

      if (tempFilters.useModeration) {
        posts = posts.filter((post) => {
          return !post.poster?.isBlacklisted;
        });
      }

      return {
        posts,
      };
    },
    [effectivePublicKey, getPosts, NON_BLOCKED_FILTER, tempFilters],
  );

  const getPostsFromList = useCallback(
    async ({
      condition,
      listName,
      filters,
      orderBy = PostsOrderBy.TimestampDesc,
      offset = 0,
    }: GetPostsFromListDTO = {}) => {
      if (!listName) {
        return {
          posts: [],
        };
      }

      const data = await getPosts({
        condition,
        filters: {
          poster: {
            and: [
              {
                focusListsAsMember: {
                  some: {
                    list: {
                      listName: {
                        equalTo: listName,
                      },
                      userPublicKeyBase58Check: {
                        equalTo: effectivePublicKey,
                      },
                    },
                  },
                },
              },
              NON_BLOCKED_FILTER,
            ],
          },
          ...MAIN_FEED_BASE_FILTERS,
          ...filters,
        },
        orderBy,
        offset,
      });

      let posts = data?.posts?.nodes.filter(Boolean) || [];

      if (tempFilters.hideNSFW === "hide") {
        posts = posts.filter((post) => {
          return (
            post.nsfwCount?.nodes.length === 0 &&
            post.poster?.isNSFW.totalCount === 0
          );
        });
      }

      if (tempFilters.useModeration) {
        posts = posts.filter((post) => {
          return !post.poster?.isBlacklisted;
        });
      }

      return {
        posts,
      };
    },
    [NON_BLOCKED_FILTER, effectivePublicKey, getPosts, tempFilters],
  );

  const getBasePostsRequest = useCallback(
    async ({
      condition,
      filters,
      orderBy = PostsOrderBy.TimestampDesc,
      offset = 0,
    }: GetPostsDTO) => {
      const data = await getPosts({
        condition,
        filters: {
          poster: {
            ...NON_BLOCKED_FILTER,
          },
          ...MAIN_FEED_BASE_FILTERS,
          ...filters,
        },
        orderBy,
        offset,
      });

      let posts = data?.posts?.nodes.filter(Boolean) || [];

      if (tempFilters.hideNSFW === "hide") {
        posts = posts.filter((post) => {
          return (
            post.nsfwCount?.nodes.length === 0 &&
            post.poster?.isNSFW.totalCount === 0
          );
        });
      }

      if (tempFilters.useModeration) {
        posts = posts.filter((post) => {
          return !post.poster?.isBlacklisted;
        });
      }

      return {
        posts,
      };
    },
    [NON_BLOCKED_FILTER, getPosts, tempFilters],
  );

  const getAdminPinnedPosts = useCallback(
    async ({
      condition,
      filters,
      orderBy = PostsOrderBy.TimestampDesc,
      offset = 0,
    }: GetPostsDTO) => {
      const data = await getPosts({
        condition,
        filters: {
          postAssociationsByPostHash: {
            some: {
              associationType: {
                equalTo: AssociationType.pinnedPost,
              },
              app: {
                publicKey: {
                  equalTo: process.env.NEXT_PUBLIC_FOCUS_APP_PUBLIC_KEY,
                },
              },
              transactor: {
                publicKey: {
                  equalTo: process.env.NEXT_PUBLIC_FOCUS_APP_PUBLIC_KEY,
                },
              },
            },
          },
          ...MAIN_FEED_BASE_FILTERS,
          ...filters,
        },
        orderBy,
        offset,
      });

      return {
        posts: data?.posts?.nodes.filter(Boolean) || [],
      };
    },
    [getPosts],
  );

  return {
    getAdminPinnedPosts,
    getBasePostsRequest,
    getPostsFromCustomFeed,
    getPostsFromPreset,
    getPostsFromList,
    getPostsFromSubscribers,
    getPosts,
    getPostsFromFollowingPeople,
    getPostsForYouFeed,
    getPostsNew,
  };
};

export type UseGetPostMethod = (params: GetPostsDTO) => Promise<{
  posts: PostType[];
  feedName?: string;
  feedProvider?: string;
}>;
